import { z } from 'zod';
import { ATTACHMENT_CONTENT_TYPES } from '../constants/attachment.constant';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C2b §3] A FILE ATTACHED TO AN ARRANGEMENT — THE FIRST FILE HANDLING IN THIS SYSTEM.
 *
 *  ⚠️ THERE IS NO INPUT SCHEMA, AND THAT IS THE INTERESTING HALF. Every other write in this
 *  repository parses its body with zod into `res.locals.validated`; a multipart upload cannot
 *  be, because the body is a STREAM that must be bounded while it arrives rather than after.
 *  So the parse is `middlewares/upload.middleware.ts` — one file, which is the same shape §10
 *  uses for every other rule it cannot express as a schema — and it writes to
 *  `res.locals.upload`, NEVER to `req.body` or `req.file`. That is verbatim why
 *  `SCAFFOLD_VERSIONS.md` picked `busboy` over `multer`.
 *
 *  ⚠️ AND THE DOWNLOAD IS THE ONE RESPONSE IN THE SYSTEM THAT IS NOT AN ENVELOPE — true
 *  again as of [FIX-204], which found two `204`s that had quietly made it false and gave them
 *  envelopes rather than weakening the rule. §6 says
 *  nothing but `ok()`/`fail()`/`paginated()` constructs a response body; a PDF is bytes, and
 *  wrapping it would mean base64 in JSON — a third larger, buffered whole in memory, and
 *  unopenable by the browser's own viewer. The REFUSALS on that route are still envelopes, so
 *  a client parses JSON exactly when the status is not 200.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const attachmentSchema = z.object({
  id: objectIdSchema,
  /**
   * ⚠️ THE UPLOADER'S OWN NAME, SANITISED — never a minted one. An operator looking for
   * "Route map.pdf" is looking for the name they gave it; a UUID is unfindable on a screen
   * that lists four of them. What sanitising removes is path separators, control characters
   * and leading dots — the parts that are dangerous rather than the parts that are useful.
   */
  filename: z.string(),
  contentType: z.enum(ATTACHMENT_CONTENT_TYPES),
  sizeBytes: z.number().int().min(0),
  /**
   * ⚠️ A REQUIRED KEY WITH A NULLABLE VALUE — §6's usual rule. The list renders a labelled
   * "Added by" row either way, and an officer deactivated since the upload still resolves.
   */
  uploadedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
  uploadedAt: z.string(),
});
export type AttachmentDto = z.infer<typeof attachmentSchema>;

export const attachmentListSchema = z.object({
  items: z.array(attachmentSchema),
  /**
   * ⚠️ BOUNDED AND UNPAGINATED, WHICH §7 PERMITS FOR EXACTLY THIS SHAPE. A page of an order's
   * attachments is not a coherent answer — there are at most ten and the screen shows all of
   * them — and the bound is a SERVER CONSTANT rather than a `limit` the caller can raise.
   * `GET /officers/:id/postings` is the standing precedent.
   */
  total: z.number().int().min(0),
});
export type AttachmentListDto = z.infer<typeof attachmentListSchema>;
